// Client portal-user management for a single organization. Expects an :id org
// route param. Lists the org's portal users and (when the viewer holds
// portal.manage_org_users for THIS org) lets them invite new accounts, change a
// user's role tier, and activate/deactivate accounts.

import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import type { PortalUser, PortalUserRole } from '@dsr/shared-types';
import {
  usePortalUsers,
  useCreatePortalUser,
  useUpdatePortalUser,
} from '../api/queries/portal';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { toast } from '../components/Toasts';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  Table,
} from '../components/ui';

const ROLE_OPTIONS: { value: PortalUserRole; label: string }[] = [
  { value: 'client_admin', label: 'Admin' },
  { value: 'client_viewer', label: 'Viewer' },
  { value: 'client_billing', label: 'Billing' },
];

const ROLE_LABEL: Record<PortalUserRole, string> = {
  client_admin: 'Admin',
  client_viewer: 'Viewer',
  client_billing: 'Billing',
};

export function PortalUsersPage(): JSX.Element {
  const params = useParams();
  const orgId = Number(params['id']);
  const valid = Number.isInteger(orgId) && orgId > 0;

  const canManage = valid && can('portal.manage_org_users', orgId);

  const { data, isPending, isError, error } = usePortalUsers(valid ? orgId : null);
  const updateUser = useUpdatePortalUser();

  const [showInvite, setShowInvite] = useState(false);
  const [deactivating, setDeactivating] = useState<PortalUser | null>(null);

  if (!valid) {
    return <div className="page-state page-error">Invalid organization id.</div>;
  }
  if (isPending) {
    return <div className="page-state">Loading portal users…</div>;
  }
  if (isError || !data) {
    return (
      <div className="page-state page-error" role="alert">
        {error instanceof ApiError ? error.message : 'Failed to load portal users.'}
      </div>
    );
  }

  const users = data.portal_users;

  const changeRole = (u: PortalUser, role: PortalUserRole): void => {
    if (role === u.role) return;
    updateUser.mutate(
      { orgId, userId: u.user_id, role },
      {
        onSuccess: () => toast.success('Role updated.'),
        onError: (err) =>
          toast.danger(err instanceof ApiError ? err.message : 'Could not update the role.'),
      },
    );
  };

  const setActive = (u: PortalUser, active: boolean): void => {
    updateUser.mutate(
      { orgId, userId: u.user_id, active },
      {
        onSuccess: () => {
          toast.success(active ? 'Account reactivated.' : 'Account deactivated.');
          setDeactivating(null);
        },
        onError: (err) =>
          toast.danger(err instanceof ApiError ? err.message : 'Could not update the account.'),
      },
    );
  };

  return (
    <div className="page portal-users-page">
      <header className="page-header">
        <div>
          <h1>Portal users</h1>
          <p className="page-sub">Client portal accounts for this organization.</p>
        </div>
        {canManage && (
          <div className="header-actions">
            <Button variant="primary" onClick={() => setShowInvite(true)}>
              Invite user
            </Button>
          </div>
        )}
      </header>

      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeaderCell>Email</Table.HeaderCell>
            <Table.HeaderCell>Name</Table.HeaderCell>
            <Table.HeaderCell>Role</Table.HeaderCell>
            <Table.HeaderCell>Active</Table.HeaderCell>
            {canManage && <Table.HeaderCell />}
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {users.length === 0 && (
            <Table.Empty colSpan={canManage ? 5 : 4}>
              No portal users for this organization.
            </Table.Empty>
          )}
          {users.map((u) => (
            <Table.Row key={u.user_id}>
              <Table.Cell tone="strong">{u.email}</Table.Cell>
              <Table.Cell>{u.display_name}</Table.Cell>
              <Table.Cell>
                {canManage ? (
                  <select
                    className="form-input"
                    value={u.role}
                    disabled={updateUser.isPending}
                    onChange={(e) => changeRole(u, e.target.value as PortalUserRole)}
                  >
                    {ROLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  ROLE_LABEL[u.role]
                )}
              </Table.Cell>
              <Table.Cell>
                {u.active ? (
                  <Badge tone="success">Active</Badge>
                ) : (
                  <Badge tone="danger">Inactive</Badge>
                )}
              </Table.Cell>
              {canManage && (
                <Table.Cell className="table-actions">
                  {u.active ? (
                    <Button
                      variant="danger"
                      onClick={() => setDeactivating(u)}
                    >
                      Deactivate
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      disabled={updateUser.isPending}
                      onClick={() => setActive(u, true)}
                    >
                      Reactivate
                    </Button>
                  )}
                </Table.Cell>
              )}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {showInvite && (
        <InviteModal orgId={orgId} onClose={() => setShowInvite(false)} />
      )}

      <ConfirmDialog
        open={deactivating !== null}
        title="Deactivate portal user"
        message={
          deactivating
            ? `Deactivate ${deactivating.display_name} (${deactivating.email})? They will lose portal access until reactivated.`
            : ''
        }
        confirmLabel="Deactivate"
        danger
        busy={updateUser.isPending}
        onConfirm={() => deactivating && setActive(deactivating, false)}
        onCancel={() => setDeactivating(null)}
      />
    </div>
  );
}

// ===========================================================================
// Invite modal (CreatePortalUserInput)
// ===========================================================================

function InviteModal({
  orgId,
  onClose,
}: {
  orgId: number;
  onClose: () => void;
}): JSX.Element {
  const create = useCreatePortalUser();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<PortalUserRole>('client_viewer');
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setFormError(null);
    if (!email.trim() || !displayName.trim()) {
      setFormError('Email and display name are required.');
      return;
    }
    create.mutate(
      {
        orgId,
        email: email.trim(),
        display_name: displayName.trim(),
        role,
        ...(firstName.trim() ? { first_name: firstName.trim() } : {}),
        ...(lastName.trim() ? { last_name: lastName.trim() } : {}),
      },
      {
        onSuccess: () => {
          toast.success('Portal user invited.');
          onClose();
        },
        onError: (err) =>
          setFormError(err instanceof ApiError ? err.message : 'Could not invite the user.'),
      },
    );
  };

  return (
    <Dialog
      title="Invite portal user"
      open
      busy={create.isPending}
      onClose={() => {
        if (!create.isPending) onClose();
      }}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="invite-portal-user"
            variant="primary"
            disabled={create.isPending}
          >
            {create.isPending ? 'Inviting…' : 'Send invite'}
          </Button>
        </>
      }
    >
      <form id="invite-portal-user" className="form-grid" onSubmit={onSubmit}>
        <FormField label="Email" required>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </FormField>
        <FormField label="Display name" required>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
          />
        </FormField>
        <FormField label="First name">
          <Input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </FormField>
        <FormField label="Last name">
          <Input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </FormField>
        <FormField label="Role">
          <select
            className="form-input"
            value={role}
            onChange={(e) => setRole(e.target.value as PortalUserRole)}
          >
            {ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </FormField>
        {formError && (
          <p className="form-error error-text" role="alert">
            {formError}
          </p>
        )}
      </form>
    </Dialog>
  );
}
